import { useMutation } from '@tanstack/react-query';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';

import { applyServerErrors } from './form-errors';

interface FormMutationOptions<TInput extends FieldValues, TOutput, TResult> {
  /** ``TInput`` is what the fields hold, ``TOutput`` what the schema resolves them to. */
  form: UseFormReturn<TInput, unknown, TOutput>;
  /** Form fields the server may report errors on. */
  fields: ReadonlyArray<Path<TInput>>;
  mutationFn: (values: TOutput) => Promise<TResult>;
  onSuccess: (result: TResult) => void | Promise<void>;
}

/** A form submit: server field errors go to the fields, the rest to ``errors.root``. */
export function useFormMutation<TInput extends FieldValues, TOutput, TResult>({
  form,
  fields,
  mutationFn,
  onSuccess,
}: FormMutationOptions<TInput, TOutput, TResult>) {
  return useMutation({
    mutationFn,
    onSuccess,
    onError: (error) => {
      const message = applyServerErrors(error, form.setError, fields);
      if (message) form.setError('root', { message });
    },
  });
}
